import { useQuery, UseQueryResult } from "@tanstack/react-query"
import { trpcRetry } from "#/lib/trpc/retryPolicy"
import { getHeap } from "../utils/heap"
import { ProviderFinderParam, ServiceFormat } from "../utils/providerFinderSearchUrl"
import { Provider } from "../types/Providers"
import ResponseError from "../types/ResponseError"
import useGetBenefit from "./useGetBenefit"
import useGetCustomNetworks from "#/components/views/app/navigation/hooks/useGetCustomNetworks"
import { trpc } from "#/lib/trpc/client"

function parseServiceFormat(value: string | null): "virtual_only" | "in_person" | "in_person_and_virtual" | undefined {
  switch (value) {
    case ServiceFormat.VirtualOnly:
      return "virtual_only"
    case ServiceFormat.InPerson:
      return "in_person"
    case ServiceFormat.InPersonAndVirtual:
      return "in_person_and_virtual"
    default:
      return undefined
  }
}

function parseNumberOrUndefined(value: string | null): number | undefined {
  if (value == null || value === "") return undefined
  const n = Number(value)
  return Number.isFinite(n) ? n : undefined
}

function parseBooleanOrUndefined(value: string | null): boolean | undefined {
  if (value == null || value === "") return undefined
  return value === "true"
}

export default function useGetProviders(params: URLSearchParams): UseQueryResult<Provider[], ResponseError> {
  const { data: benefit, isLoading, isError } = useGetBenefit()
  const {
    data: customNetworksData,
    isLoading: isCustomNetworksLoading,
    isError: isCustomNetworksError
  } = useGetCustomNetworks()
  const isCustomNetworks = !isCustomNetworksLoading && !isCustomNetworksError && customNetworksData?.hasCustomNetworks
  const isCarrotCore = !isLoading && !isError && benefit.program.isCarrotLite

  const latitude = params.get(ProviderFinderParam.Latitude)
  const longitude = params.get(ProviderFinderParam.Longitude)
  const locationCountryCode = params.get(ProviderFinderParam.Country)
  const stateCode = params.get(ProviderFinderParam.State)
  const radiusInKm = params.get(ProviderFinderParam.RadiusInKm)
  const companyCountryCode = params.get("companyCountryCode")
  const carrotPartner = params.get(ProviderFinderParam.CarrotPartner)
  const providerType = params.get(ProviderFinderParam.ProviderType)
  const serviceFormat = params.get(ProviderFinderParam.ServiceFormat)
  const doulaServices = params.get(ProviderFinderParam.DoulaServices)
  const carrotCenterOfExcellence = params.get(ProviderFinderParam.CarrotCenterOfExcellence)
  const acceptsDirectPay = params.get(ProviderFinderParam.AcceptsDirectPay)
  const providerNetworkId = params.get(ProviderFinderParam.ProviderNetworkId)

  // Carrot Core members outside any custom network can't keyword-search.
  if (isCarrotCore && !isCustomNetworks) {
    params.delete(ProviderFinderParam.Keyword)
    params.delete(ProviderFinderParam.ProviderId)
  }
  const keyword = params.get(ProviderFinderParam.Keyword)
  const providerId = params.get(ProviderFinderParam.ProviderId)

  return useQuery<Provider[], ResponseError>(
    [
      "getProviders",
      latitude,
      longitude,
      locationCountryCode,
      stateCode,
      radiusInKm,
      carrotPartner,
      providerType,
      keyword,
      providerId,
      serviceFormat,
      doulaServices,
      carrotCenterOfExcellence,
      acceptsDirectPay,
      providerNetworkId,
      companyCountryCode,
      isCarrotCore,
      isCustomNetworks
    ],
    async () => {
      if (providerType === "NONE" || locationCountryCode !== companyCountryCode) {
        return []
      }

      // Member picked a specific provider from the typeahead — hydrate by id
      // so the results list is exactly that provider, not a fuzzy re-search
      // of every same-named record. lat/lng still flow through so distance
      // sorts correctly.
      const providerIdNumber = parseNumberOrUndefined(providerId)
      if (providerIdNumber != null) {
        const result = await trpc.providerFinder.byIds.query({
          ids: [providerIdNumber],
          lat: parseNumberOrUndefined(latitude),
          lng: parseNumberOrUndefined(longitude),
          providerType: providerType || undefined
        })

        getHeap().track("Provider Search Results Loaded", {
          "Provider Count": result.data.length,
          "Selection Source": "typeahead"
        })

        return result.data
      }

      const result = await trpc.providerFinder.search.query({
        name: keyword || undefined,
        lat: parseNumberOrUndefined(latitude),
        lng: parseNumberOrUndefined(longitude),
        radiusInKm: parseNumberOrUndefined(radiusInKm),
        countryCode: locationCountryCode || undefined,
        stateCode: stateCode || undefined,
        carrotPartner: parseBooleanOrUndefined(carrotPartner),
        providerType: providerType || undefined,
        services: doulaServices || undefined,
        acceptsDirectPay: parseBooleanOrUndefined(acceptsDirectPay),
        carrotCenterOfExcellence: parseBooleanOrUndefined(carrotCenterOfExcellence),
        providerNetworkName: providerNetworkId || undefined,
        serviceFormat: parseServiceFormat(serviceFormat)
      })

      getHeap().track("Provider Search Results Loaded", {
        "Provider Count": result.data.length,
        "Selection Source": "keyword"
      })

      return result.data
    },
    {
      refetchOnWindowFocus: false,
      retry: trpcRetry(1)
    }
  )
}
